/**
 * まなびモードの進捗(docs/10_paltown_platform.md §2)。
 *
 * 単元ごとの**最高星(0〜3)**と、**問題ごとのまちがい記録**(ふくしゅう用)を持つ。
 * 星のしきい値も苦手判定も `@paltyping/quiz`(描画・保存先に依存しない純TS)にあり、
 * ここは「保存する / 読み直す」だけを担当する(CLAUDE.md ルール3)。
 *
 * いまは **ローカル保存のみ**(`services/puzzleProgress.ts` と同じ段階)。
 * **コインの付与はすでにサーバー側**(callable `submitQuizResult` / `services/quizRewards.ts`)で、
 * 星ボーナスの重複防止に使う「単元別の最高星」も Functions 側の進捗が正
 * (`users/{uid}/progress/paltown-quiz`)。ここが持つ星は**表示用のキャッシュ**にすぎない。
 *
 * TODO(docs/10 §2): 単元の星・正解数の**読み出し**もサーバー同期にする
 * (機種を変えても続きから遊べるようにする)。コインは絶対にクライアントで足さないこと
 * (CLAUDE.md ルール2)。
 */

import {
  firstQuizStageId,
  parseQuizStageId,
  pruneQuizStats,
  quizStars,
  quizUnitStages,
  recordQuizAnswers,
  selectReviewQuestions,
  weakQuestionIds,
  type QuizAnsweredQuestion,
  type QuizQuestion,
  type QuizQuestionStat,
  type QuizReviewOptions,
  type QuizUnit,
} from '@paltyping/quiz';
import { isDebugUnlockAll } from './debugUnlock';
import type { KeyValueStorage } from './progress';
import { appStorage, recordUserStateOperation, withoutUserStateNotify } from './userState';

/** 単元1つぶんの記録(最高記録だけを残す)。 */
export interface QuizUnitRecord {
  /** これまでの最高星(0〜3)。 */
  readonly stars: number;
  /** そのときのせいかい数。 */
  readonly bestCorrect: number;
  /** 出題数(ふつうは `QUIZ_UNIT.sessionQuestions`)。 */
  readonly total: number;
  /** あそんだ回数。 */
  readonly plays: number;
}

/** 進捗スナップショット。 */
export interface QuizProgressSnapshot {
  readonly version: number;
  /**
   * 単元ID → 記録。**星0でも1回あそんだら入る**(あそんだ事実を残す)。
   *
   * ステージ制(docs/13 §11)より前はここが唯一の記録だった。いまは
   * **ステージの記録が正**(`stages`)で、ここは
   *   - ステージ制より前に遊んだ子の星の置き場(= ステージ1の星として読む)
   *   - 単元をまたいだ「あそんだ回数」の集計
   * として残す。既存キーを壊さないので、古い保存値をそのまま読み続けられる。
   */
  readonly units: Readonly<Record<string, QuizUnitRecord>>;
  /**
   * ステージID(`{単元ID}-s{番号}`)→ 記録(docs/13 §11)。
   *
   * **ステージ1だけは特別**:ここに無ければ `units[単元ID]` の星を読む
   * (= 既存の単元星をステージ1の星として lazy 移行する。`stageRecordOf`)。
   * 移行スクリプトは走らせない —— 読むたびに古い形を新しい意味で解釈するだけで、
   * 一度でもステージ1を遊べば `stages` 側に書かれて自然に移り終わる。
   */
  readonly stages: Readonly<Record<string, QuizUnitRecord>>;
  /**
   * 問題ID → 解答履歴(ふくしゅう用のまちがい記録)。
   *
   * 件数は `QUIZ_REVIEW.maxTracked` で頭打ちにする(問題は今後数千問に増える)。
   * あふれたぶんは「解けている・古い」ものから捨てる = 苦手と直近性だけが残る。
   */
  readonly questions: Readonly<Record<string, QuizQuestionStat>>;
  /** あそびかたのデモを見終わったか(2回目からは自動では出さない)。 */
  readonly demoSeen: boolean;
  /**
   * 「こたえを みる」を押した回数(docs/07_learning_support.md §3.2 / §7.4)。
   *
   * §3.2「押下はイベント記録され、レポートの**自力解決率**に反映」の材料。
   * 大げさな基盤は作らず、**この1フィールドだけ**を進捗に足してある
   * (解いた問題数はすでに `questions` にあるので、割り算の分子になる)。
   * 押した問題そのものは**不正解として**ふつうの誤答フローに乗る
   * (`questions` の wrong が増え、にがてプールに入る)ので、ここは回数だけ。
   */
  readonly revealed: number;
}

/** 1ステージぶんの成績(保存する形)。 */
export interface QuizUnitResult {
  readonly unitId: string;
  /**
   * ステージID(docs/13 §11)。省略したら**その単元のステージ1**として記録する
   * (ステージを知らない呼び出し側との互換)。
   */
  readonly stageId?: string;
  readonly correct: number;
  readonly total: number;
  /** 問題ごとの正誤(ふくしゅうの材料。省略したら記録しない)。 */
  readonly answers?: readonly QuizAnsweredQuestion[];
}

export const EMPTY_QUIZ_PROGRESS: QuizProgressSnapshot = {
  version: 1,
  units: {},
  stages: {},
  questions: {},
  demoSeen: false,
  revealed: 0,
};

/** 保存キー。 */
const STORAGE_KEY = 'paltyping.quizProgress.v1';

// ── 参照ヘルパ ─────────────────────────────────────────

/** 単元の記録(まだあそんでいなければ undefined)。 */
export function unitRecordOf(
  snapshot: QuizProgressSnapshot,
  unitId: string,
): QuizUnitRecord | undefined {
  return snapshot.units[unitId];
}

/** 単元の最高星(0〜3)。 */
export function unitStarsOf(snapshot: QuizProgressSnapshot, unitId: string): number {
  return snapshot.units[unitId]?.stars ?? 0;
}

/** 単元をクリア済みか(星1以上)。 */
export function isUnitCleared(snapshot: QuizProgressSnapshot, unitId: string): boolean {
  return unitStarsOf(snapshot, unitId) >= 1;
}

// ── 単元内ステージ(docs/13 §11)─────────────────────────

/** ステージの記録(まだあそんでいなければ undefined)。 */
export function stageRecordOf(
  snapshot: QuizProgressSnapshot,
  stageId: string,
): QuizUnitRecord | undefined {
  return snapshot.stages[stageId];
}

/**
 * 既存の単元星を**ステージ1の星**として取り込む(docs/13 §11 の lazy 移行)。
 *
 * ステージ制より前の保存値は単元ごとの星しか持っていない。読み込むたびに
 * 「その単元にステージの記録が1つも無ければ、単元の記録をステージ1の記録として置く」
 * だけの冪等な処理を通す(移行スクリプトは走らせない。`units` は消さない)。
 *
 * ステージ2以降は移行のしようがない —— 前は単元プール全体から10問を抽選していたので、
 * 「どのステージを解いたことになるのか」が決められない。0から始めてもらう。
 * ステージ1がクリア済みとして復元されるので、**続きはすぐ遊べる**。
 */
export function migrateQuizStages(snapshot: QuizProgressSnapshot): QuizProgressSnapshot {
  /** すでにステージの記録がある単元(移行の対象外)。 */
  const touched = new Set<string>();
  for (const stageId of Object.keys(snapshot.stages)) {
    const parsed = parseQuizStageId(stageId);
    if (parsed) touched.add(parsed.unitId);
  }

  const stages: Record<string, QuizUnitRecord> = { ...snapshot.stages };
  let changed = false;
  for (const [unitId, record] of Object.entries(snapshot.units)) {
    if (touched.has(unitId)) continue;
    stages[firstQuizStageId(unitId)] = record;
    changed = true;
  }
  return changed ? { ...snapshot, stages } : snapshot;
}

/** ステージの最高星(0〜3)。 */
export function stageStarsOf(snapshot: QuizProgressSnapshot, stageId: string): number {
  return stageRecordOf(snapshot, stageId)?.stars ?? 0;
}

/** ステージをクリア済みか(星1以上)。 */
export function isStageCleared(snapshot: QuizProgressSnapshot, stageId: string): boolean {
  return stageStarsOf(snapshot, stageId) >= 1;
}

/** 単元の進み具合(クリア済みステージ数 / ステージ数)。単元ボタンの塗りに使う。 */
export function unitStageProgressOf(
  snapshot: QuizProgressSnapshot,
  unit: QuizUnit,
): { readonly cleared: number; readonly total: number } {
  const stages = quizUnitStages(unit);
  return {
    cleared: stages.filter((stage) => isStageCleared(snapshot, stage.id)).length,
    total: stages.length,
  };
}

/**
 * ステージが解放されているか(docs/13 §11)。
 *
 * ステージ1はいつでも遊べて、n+1 は **n をクリア(★1以上)**すると開く。
 * `?debugUnlock=1` のときは常に true(進捗データには触らない非破壊のフラグ)。
 */
export function isQuizStageUnlocked(
  unit: QuizUnit,
  stageId: string,
  snapshot: QuizProgressSnapshot,
  debugUnlockAll: boolean = isDebugUnlockAll(),
): boolean {
  if (debugUnlockAll) return true;
  const stages = quizUnitStages(unit);
  const index = stages.findIndex((stage) => stage.id === stageId);
  if (index <= 0) return index === 0;
  const previous = stages[index - 1];
  return previous !== undefined && isStageCleared(snapshot, previous.id);
}

/** 単元に1つでもクリア済みステージがあるか(つぎの単元の解放条件)。 */
export function hasClearedStage(snapshot: QuizProgressSnapshot, unit: QuizUnit): boolean {
  return quizUnitStages(unit).some((stage) => isStageCleared(snapshot, stage.id));
}

/**
 * 単元が解放されているか(docs/13 §11 で条件をゆるめた)。
 *
 * **教科の中で順に解放**する。先頭の単元はいつでも遊べて、2つめ以降は
 * 直前の単元に**クリア済みのステージが1つでもあれば**開く
 * (全ステージのクリアは強制しない。反復量を増やしたぶん、先へ進む道を塞がない)。
 * `?debugUnlock=1` のときは常に true。
 *
 * @param units その教科の単元(学習順)
 */
export function isQuizUnitUnlocked(
  units: readonly QuizUnit[],
  unitId: string,
  snapshot: QuizProgressSnapshot,
  debugUnlockAll: boolean = isDebugUnlockAll(),
): boolean {
  if (debugUnlockAll) return true;
  const index = units.findIndex((unit) => unit.id === unitId);
  if (index <= 0) return true;
  const previous = units[index - 1];
  return previous === undefined || hasClearedStage(snapshot, previous);
}

/** 単元の並びに対して、獲得した星の合計。 */
export function totalQuizStars(snapshot: QuizProgressSnapshot, units: readonly QuizUnit[]): number {
  return units.reduce((sum, unit) => sum + unitStarsOf(snapshot, unit.id), 0);
}

// ── 更新(純関数) ─────────────────────────────────────

/** 前の記録に今回のぶんを重ねる(星・せいかい数は最高記録だけを残す)。 */
function mergeRecord(
  previous: QuizUnitRecord | undefined,
  stars: number,
  result: QuizUnitResult,
): QuizUnitRecord {
  return {
    stars: Math.max(stars, previous?.stars ?? 0),
    bestCorrect: Math.max(result.correct, previous?.bestCorrect ?? 0),
    total: Math.max(result.total, previous?.total ?? 0),
    plays: (previous?.plays ?? 0) + 1,
  };
}

/**
 * 1回ぶんの成績を反映した**新しい**スナップショットを返す。
 *
 * 星は**下がらない**(最高記録を残す)。せいかい数も最高記録だけを持つ。
 * 「もういちど」で失敗しても、前に取った星は消えない。
 *
 * 書くのは2か所(docs/13 §11):
 *   - `stages[ステージID]` … **こちらが正**。解放と塗り進捗はここを見る
 *   - `units[単元ID]` …… 単元の最高星・あそんだ回数の集計(既存キーを壊さない)
 * ステージ1の記録は、古い保存値(単元星だけ)を引き継いでから重ねる
 * (`stageRecordOf` の lazy 移行と同じ規則で、星が巻き戻らない)。
 */
export function applyQuizUnitResult(
  snapshot: QuizProgressSnapshot,
  result: QuizUnitResult,
): QuizProgressSnapshot {
  const stars = quizStars(result.correct, result.total);
  const stageId = result.stageId ?? firstQuizStageId(result.unitId);
  return {
    ...snapshot,
    units: {
      ...snapshot.units,
      [result.unitId]: mergeRecord(snapshot.units[result.unitId], stars, result),
    },
    stages: {
      ...snapshot.stages,
      [stageId]: mergeRecord(stageRecordOf(snapshot, stageId), stars, result),
    },
  };
}

/**
 * 問題ごとの正誤を記録した**新しい**スナップショットを返す。
 *
 * 判定も件数の間引きも `@paltyping/quiz` の純関数がやる。ここは
 * 「Record ⇄ Map の詰め替え」しかしない(CLAUDE.md ルール1 と同じ思想)。
 */
export function applyQuizAnswers(
  snapshot: QuizProgressSnapshot,
  answers: readonly QuizAnsweredQuestion[],
  at: number,
): QuizProgressSnapshot {
  if (answers.length === 0) return snapshot;
  const recorded = recordQuizAnswers(new Map(Object.entries(snapshot.questions)), answers, at);
  const kept = pruneQuizStats(recorded.values());
  const questions: Record<string, QuizQuestionStat> = {};
  for (const stat of kept) questions[stat.questionId] = stat;
  return { ...snapshot, questions };
}

/** 記録のある問題の履歴(順不同)。 */
export function questionStatsOf(snapshot: QuizProgressSnapshot): QuizQuestionStat[] {
  return Object.values(snapshot.questions);
}

/** にがてな問題のID(ふくしゅうの優先度順)。 */
export function weakQuestionIdsOf(snapshot: QuizProgressSnapshot): string[] {
  return weakQuestionIds(questionStatsOf(snapshot));
}

/**
 * いま出せるふくしゅうの問題(優先度順・最大 `QUIZ_REVIEW.sessionQuestions` 問)。
 *
 * **手元のバンクにある問題からしか選べない**ので、単元えらび画面の
 * 「にがてな もんだい ◯もん」もこの結果の件数を見せる(押したら出る数と必ず一致する)。
 */
export function reviewQuestionsOf(
  snapshot: QuizProgressSnapshot,
  questions: readonly QuizQuestion[],
  options: QuizReviewOptions = {},
): QuizQuestion[] {
  return selectReviewQuestions(questions, questionStatsOf(snapshot), options);
}

/**
 * 「こたえを みる」を1回数えた新しいスナップショット(docs/07 §3.2)。
 *
 * 数えるだけ。正誤の記録は `applyQuizAnswers`(ふつうの誤答フロー)が持つ。
 */
export function markQuizAnswerRevealed(snapshot: QuizProgressSnapshot): QuizProgressSnapshot {
  return { ...snapshot, revealed: (snapshot.revealed ?? 0) + 1 };
}

/** デモ既読を立てた新しいスナップショット。 */
export function markQuizDemoSeen(snapshot: QuizProgressSnapshot): QuizProgressSnapshot {
  if (snapshot.demoSeen) return snapshot;
  return { ...snapshot, demoSeen: true };
}

// ── 保存形式 ───────────────────────────────────────────

/** 数値として妥当なら丸めて範囲に収める(壊れた保存値を捨てるため)。 */
function safeNumber(value: unknown, max: number): number {
  if (typeof value !== 'number' || !Number.isFinite(value)) return 0;
  return Math.max(0, Math.min(max, Math.trunc(value)));
}

/**
 * 問題ごとの履歴の保存形。**キーを1文字**にしてある。
 *
 * 数百問ぶんを localStorage に置くので、`{"c":3,"w":1,"s":2,"t":175…}` の形で
 * 1問あたり45バイト前後に抑える(`QUIZ_REVIEW.maxTracked` = 300 で 15KB 程度)。
 * 問題IDは Record のキーなので値には持たない。
 */
interface StoredQuestionStat {
  /** correct */
  readonly c: number;
  /** wrong */
  readonly w: number;
  /** streak(直近の連続せいかい。0 = 直近まちがえた) */
  readonly s: number;
  /** lastAt(epoch ms) */
  readonly t: number;
}

function serializeQuestionStats(
  questions: Readonly<Record<string, QuizQuestionStat>>,
): Record<string, StoredQuestionStat> {
  const out: Record<string, StoredQuestionStat> = {};
  for (const [id, stat] of Object.entries(questions)) {
    out[id] = { c: stat.correct, w: stat.wrong, s: stat.streak, t: stat.lastAt };
  }
  return out;
}

/** 壊れた保存値を捨てつつ履歴を読み直す(上限も掛け直す)。 */
function deserializeQuestionStats(value: unknown): Record<string, QuizQuestionStat> {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) return {};
  const stats: QuizQuestionStat[] = [];
  for (const [id, raw] of Object.entries(value as Record<string, unknown>)) {
    if (typeof raw !== 'object' || raw === null || id.length === 0) continue;
    const entry = raw as Partial<StoredQuestionStat>;
    const lastAt =
      typeof entry.t === 'number' && Number.isFinite(entry.t) ? Math.trunc(entry.t) : 0;
    stats.push({
      questionId: id,
      correct: safeNumber(entry.c, 99999),
      wrong: safeNumber(entry.w, 99999),
      streak: safeNumber(entry.s, 99999),
      lastAt: Math.max(0, lastAt),
    });
  }
  // 保存側の上限が変わった / 手で書き換えられた場合もここで頭打ちにする。
  const out: Record<string, QuizQuestionStat> = {};
  for (const stat of pruneQuizStats(stats)) out[stat.questionId] = stat;
  return out;
}

/** 単元・ステージの記録(同じ形)を、壊れた値を捨てながら読み直す。 */
function deserializeRecords(value: unknown): Record<string, QuizUnitRecord> {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) return {};
  const out: Record<string, QuizUnitRecord> = {};
  for (const [id, raw] of Object.entries(value as Record<string, unknown>)) {
    if (typeof raw !== 'object' || raw === null || id.length === 0) continue;
    const entry = raw as Partial<QuizUnitRecord>;
    out[id] = {
      stars: safeNumber(entry.stars, 3),
      bestCorrect: safeNumber(entry.bestCorrect, 999),
      total: safeNumber(entry.total, 999),
      plays: safeNumber(entry.plays, 99999),
    };
  }
  return out;
}

/**
 * 壊れたJSON・未知のバージョンを捨てて既定値に落とす。
 *
 * `questions`(まちがい記録)は**あとから足したフィールド**なので、
 * 無ければ空として読む(version は上げない = 既存の子の星を消さない)。
 */
export function deserializeQuizProgress(raw: string | null): QuizProgressSnapshot {
  if (!raw) return EMPTY_QUIZ_PROGRESS;
  try {
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) {
      return EMPTY_QUIZ_PROGRESS;
    }
    const record = parsed as Partial<QuizProgressSnapshot>;
    if (record.version !== 1) return EMPTY_QUIZ_PROGRESS;

    // ステージの記録(docs/13 §11)は**あとから足したフィールド**。無ければ空で読み、
    // `migrateQuizStages` が既存の単元星をステージ1として取り込む
    // (version は上げない = 既存の子の星を消さない)。
    return migrateQuizStages({
      version: 1,
      units: deserializeRecords(record.units),
      stages: deserializeRecords((record as { stages?: unknown }).stages),
      questions: deserializeQuestionStats((record as { questions?: unknown }).questions),
      demoSeen: record.demoSeen === true,
      // 「こたえを みる」の回数も**あとから足したフィールド**(無ければ 0)。
      revealed: safeNumber((record as { revealed?: unknown }).revealed, 999_999),
    });
  } catch {
    return EMPTY_QUIZ_PROGRESS;
  }
}

/**
 * サーバーから読み戻した「上書きする項目」(docs/04_data_model.md §5.1)。
 *
 * 単元・ステージの星(と せいかい数・あそんだ回数)は**サーバーが権威**。
 * 問題ごとのにがて記録・デモ既読・「こたえを みる」の回数は
 * **ローカルにしか無い**ので、読み戻しでも触らない。
 */
export interface QuizProgressHydration {
  readonly units: Readonly<Record<string, QuizUnitRecord>>;
  readonly stages: Readonly<Record<string, QuizUnitRecord>>;
}

/** サーバーの読み戻しを端末キャッシュへ反映するときの通知制御。 */
export interface QuizProgressHydrateOptions {
  /** 読み戻し由来の保存を、次の saveUserState 操作として拾わない。 */
  readonly suppressSync?: boolean;
}

/** 読み戻しを受け取れるまなびの進捗キャッシュ。 */
export interface HydratableQuizProgress {
  hydrate(
    input: QuizProgressHydration,
    options?: QuizProgressHydrateOptions,
  ): Promise<QuizProgressSnapshot>;
  /**
   * メモリ上のキャッシュを捨てて、次の `load()` で localStorage を読み直す。
   * にがて記録・デモ既読は読み戻しで **localStorage 側**が書き換わる(docs/04 §5.1.1)。
   */
  reload(): void;
}

/** 進捗の読み書き。ローカル実装と将来の Firebase 実装を差し替えられるようにする。 */
export interface QuizProgressService {
  load(): Promise<QuizProgressSnapshot>;
  /**
   * メモリ上のキャッシュを捨てて、次の `load()` で localStorage を読み直す
   * (`HydratableQuizProgress.reload` と同じもの。読み戻しのあとに呼ぶ)。
   */
  reload(): void;
  /** 1単元ぶんの成績(と問題ごとの正誤)を記録する。 */
  saveUnitResult(result: QuizUnitResult): Promise<QuizProgressSnapshot>;
  /**
   * ふくしゅう1回ぶんを記録する。
   *
   * **単元の星には触らない**(教科をまたいだ寄せ集めなので単元の成績にならない)。
   * 問題ごとの正誤だけを足して、解けた問題が苦手プールから抜ける。
   */
  saveReviewAnswers(answers: readonly QuizAnsweredQuestion[]): Promise<QuizProgressSnapshot>;
  /** あそびかたを見たことを記録する。 */
  markDemoSeen(): Promise<QuizProgressSnapshot>;
  /**
   * 「こたえを みる」を押したことを数える(docs/07 §3.2 の自力解決率の材料)。
   * **正誤の記録とは別**(そちらは不正解としてふつうに保存される)。
   */
  markAnswerRevealed(): Promise<QuizProgressSnapshot>;
  reset(): Promise<QuizProgressSnapshot>;
}

/**
 * localStorage 実装。
 * 次フェーズで `FirebaseQuizProgressService`(callable `submitQuizResult`)に差し替える。
 */
export class LocalQuizProgressService implements QuizProgressService, HydratableQuizProgress {
  private snapshot: QuizProgressSnapshot | null = null;

  constructor(
    private readonly storage: KeyValueStorage | null = appStorage(),
    private readonly key: string = STORAGE_KEY,
    /** 現在時刻(まちがい記録の「直近日時」。テストで固定する)。 */
    private readonly now: () => number = () => Date.now(),
  ) {}

  /** 次の `load()` で localStorage を読み直す(読み戻しのあと)。 */
  reload(): void {
    this.snapshot = null;
  }

  async load(): Promise<QuizProgressSnapshot> {
    if (!this.snapshot) {
      this.snapshot = deserializeQuizProgress(this.storage?.getItem(this.key) ?? null);
    }
    return this.snapshot;
  }

  async saveUnitResult(result: QuizUnitResult): Promise<QuizProgressSnapshot> {
    // TODO(docs/07_learning_support.md): 学習イベント(問題ごとの正誤・所要時間・skills)を
    // ストリームへ流す。AIチューター・つまずき分析の材料になるが今回のスコープ外
    // (問題ごとの正誤は下でローカルに溜めていて、これがその第一歩)。
    const at = this.now();
    const answers = result.answers ?? [];
    const withAnswers = applyQuizAnswers(await this.load(), answers, at);
    this.snapshot = applyQuizUnitResult(withAnswers, result);
    this.persist();
    this.recordAnswers(answers, at);
    return this.snapshot;
  }

  async saveReviewAnswers(answers: readonly QuizAnsweredQuestion[]): Promise<QuizProgressSnapshot> {
    const at = this.now();
    this.snapshot = applyQuizAnswers(await this.load(), answers, at);
    this.persist();
    this.recordAnswers(answers, at);
    return this.snapshot;
  }

  async markDemoSeen(): Promise<QuizProgressSnapshot> {
    this.snapshot = markQuizDemoSeen(await this.load());
    this.persist();
    recordUserStateOperation({
      section: 'quizStats',
      occurredAt: this.now(),
      payload: { demoSeen: true },
    });
    return this.snapshot;
  }

  async markAnswerRevealed(): Promise<QuizProgressSnapshot> {
    this.snapshot = markQuizAnswerRevealed(await this.load());
    this.persist();
    recordUserStateOperation({
      section: 'quizStats',
      occurredAt: this.now(),
      payload: { revealedDelta: 1 },
    });
    return this.snapshot;
  }

  /**
   * サーバー権威の星で上書きする(docs/04 §5.1 の読み戻し)。
   *
   * にがて記録(`questions`)・デモ既読・「こたえを みる」の回数は端末のものなので残す。
   * ステージの記録が無い単元は `migrateQuizStages` が**ステージ1の星**として拾う
   * (サーバー側の lazy 移行と同じ規則。docs/13 §11)。
   */
  async hydrate(
    input: QuizProgressHydration,
    options: QuizProgressHydrateOptions = {},
  ): Promise<QuizProgressSnapshot> {
    const current = await this.load();
    this.snapshot = migrateQuizStages({
      ...current,
      units: input.units,
      stages: input.stages,
    });
    if (options.suppressSync) withoutUserStateNotify(() => this.persist());
    else this.persist();
    return this.snapshot;
  }

  async reset(): Promise<QuizProgressSnapshot> {
    this.snapshot = EMPTY_QUIZ_PROGRESS;
    this.storage?.removeItem(this.key);
    return this.snapshot;
  }

  private persist(): void {
    if (!this.storage || !this.snapshot) return;
    try {
      this.storage.setItem(
        this.key,
        JSON.stringify({
          ...this.snapshot,
          // まちがい記録だけは1文字キーの小さい形で書く(件数が多いので効く)。
          questions: serializeQuestionStats(this.snapshot.questions),
        }),
      );
    } catch {
      // 容量超過・プライベートモード等。進捗が消えるだけで遊べる。
    }
  }

  private recordAnswers(answers: readonly QuizAnsweredQuestion[], at: number): void {
    if (answers.length === 0) return;
    recordUserStateOperation({
      section: 'quizStats',
      occurredAt: at,
      payload: {
        answers: answers.map((answer) => ({
          questionId: answer.questionId,
          correct: answer.correct,
        })),
      },
    });
  }
}
